import type { Date as PDate } from "../../generated/core/v1/date";
import { Event_Type } from "../../generated/core/v1/events";
import { type Family, PriorityKind } from "../../generated/core/v1/family";
import { compareDates } from "../lib/dates";
import { type Plan, eventsOf } from "./core";
import { PRIORITY_KIND_LABEL } from "./entities";
import { householdRetirement } from "./retirement";

/**
 * How far each priority gets funded, from PRIORITY_DUE events (required vs received per
 * occurrence), compared between Abi's plan and the current trajectory. "Retiring comfortably" uses
 * the engine's RETIREMENT event (projected vs required nest egg).
 *
 * Impact follows Abi's own objective, utility = Σ 2^importance × min(completion, 1): a priority's
 * share of the improvement is 2^importance × (min(abi,1) − min(naive,1)) over the positive total.
 */
export type PriorityFunding = {
  /** Σ min(received, required) / Σ required over its occurrences (0..1). */
  completion: number;
  required: number;
  received: number;
  occurrences: number;
  /** First and last occurrence due dates. */
  firstDue?: PDate;
  lastDue?: PDate;
  /** When the plan reports it fully funded (PRIORITY_COMPLETED). Plans from before PRIORITY_MISSED
   * sent PRIORITY_COMPLETED at the end whether or not it was funded, so it counts only when the
   * occurrences add up to full funding. */
  completedOn?: PDate;
  /** When the plan reports it ended without full funding (PRIORITY_MISSED: for a home-buying goal,
   * the purchase skipped). Plans from before that event never have it. */
  missedOn?: PDate;
};

export type PriorityOutcome = {
  key: string; // priority uuid, or "retirement"
  name: string;
  importance: number;
  isRetirement: boolean;
  abi?: PriorityFunding;
  naive?: PriorityFunding;
  /** A home-buying goal: the price, and the down payment (stored, else what Abi's plan requires:
   * the engine's default). Funded means the plan buys the home on its date. */
  home?: { price: number; downPayment?: number; due?: PDate };
};

export function priorityFunding(plan: Plan, priorityUuid: string): PriorityFunding | undefined {
  if (!plan) return undefined;
  const dues = eventsOf(plan, Event_Type.PRIORITY_DUE).filter((e) => e.entityUuid === priorityUuid);
  const ended = (type: Event_Type) =>
    eventsOf(plan, type).find((e) => e.entityUuid === priorityUuid);
  const completed = ended(Event_Type.PRIORITY_COMPLETED);
  const missed = ended(Event_Type.PRIORITY_MISSED);
  if (!dues.length && !completed && !missed) return undefined;
  let required = 0;
  let received = 0;
  dues.forEach((e) => {
    if (e.details.oneofKind !== "priorityDue") return;
    const d = e.details.priorityDue;
    required += d.required;
    received += Math.min(d.received, d.required);
  });
  const completion = required > 0 ? received / required : completed && !missed ? 1 : 0;
  return {
    completion,
    required,
    received,
    occurrences: dues.length,
    firstDue: dues[0]?.date,
    lastDue: dues.at(-1)?.date,
    completedOn: completed && !missed && completion >= 0.995 ? completed.date : undefined,
    missedOn: missed?.date,
  };
}

/**
 * Whether Abi's plan buys a home-buying goal's home: "bought" or "skipped" on its date, undefined when
 * the plan doesn't reach it (it ran short first). The engine reports it (PRIORITY_COMPLETED or
 * PRIORITY_MISSED); for plans from before PRIORITY_MISSED, the purchase is all or nothing, so the
 * down payment received on the date says it.
 */
export function homePurchase(f: PriorityFunding | undefined): "bought" | "skipped" | undefined {
  if (!f) return undefined;
  if (f.missedOn) return "skipped";
  if (f.completedOn) return "bought";
  if (!f.occurrences) return undefined;
  return f.completion >= 0.995 ? "bought" : "skipped";
}

export function retirementFunding(
  plan: Plan,
  family: Family | undefined | null,
): PriorityFunding | undefined {
  const r = householdRetirement(plan, family);
  if (!r) return undefined;
  const required = r.details.nestEggRequired;
  const projected = r.details.projectedNestEgg;
  return {
    completion: required > 0 ? Math.min(projected, required) / required : 1,
    required,
    received: projected,
    occurrences: 1,
    firstDue: r.date,
    lastDue: r.date,
    completedOn: projected >= required ? r.date : undefined,
  };
}

export function priorityOutcomes(
  family: Family | undefined | null,
  abi: Plan,
  naive: Plan,
): PriorityOutcome[] {
  if (!family) return [];
  const out: PriorityOutcome[] = [];
  if (family.retirementPriorityLevel > 0 || householdRetirement(abi, family)) {
    out.push({
      key: "retirement",
      name: "Retiring comfortably",
      importance: family.retirementPriorityLevel,
      isRetirement: true,
      abi: retirementFunding(abi, family),
      naive: retirementFunding(naive, family),
    });
  }
  family.financialPriorities.forEach((p) => {
    out.push({
      key: p.uuid,
      name: (p.name && p.name.trim()) || PRIORITY_KIND_LABEL[p.kind] || "A priority",
      importance: p.level,
      isRetirement: false,
      abi: priorityFunding(abi, p.uuid),
      naive: priorityFunding(naive, p.uuid),
    });
  });
  family.homebuyingPriorities.forEach((p) => {
    const abiFunding = priorityFunding(abi, p.uuid);
    out.push({
      key: p.uuid,
      name: (p.name && p.name.trim()) || PRIORITY_KIND_LABEL[PriorityKind.BUY_HOME],
      importance: p.level,
      isRetirement: false,
      abi: abiFunding,
      naive: priorityFunding(naive, p.uuid),
      home: {
        price: p.housePrice,
        downPayment: p.downPayment ?? (abiFunding?.required || undefined),
        due: p.dueDate,
      },
    });
  });
  return out.sort((a, b) => b.importance - a.importance);
}

/** The change in Abi's utility a priority accounts for. */
export function utilityGain(o: PriorityOutcome): number {
  const a = Math.min(o.abi?.completion ?? 0, 1);
  const n = Math.min(o.naive?.completion ?? 0, 1);
  return Math.pow(2, o.importance) * (a - n);
}

export type ImpactShare = { outcome: PriorityOutcome; gain: number; share: number };

/** Each improving priority's share of the plan's total improvement, largest first. */
export function impactShares(outcomes: PriorityOutcome[]): ImpactShare[] {
  const gains = outcomes
    .map((o) => ({ outcome: o, gain: utilityGain(o) }))
    .filter((g) => g.gain > 0);
  const total = gains.reduce((s, g) => s + g.gain, 0) || 1;
  return gains.map((g) => ({ ...g, share: g.gain / total })).sort((a, b) => b.share - a.share);
}

/** The plan's share of spare money a 1-10 importance gets (2^level / Σ 2^level), for the form hint. */
export function weightShare(level: number, allLevels: number[]): number | undefined {
  const scored = allLevels.filter((l) => l > 0);
  if (!level || !scored.length) return undefined;
  const total = scored.reduce((s, l) => s + Math.pow(2, l), 0);
  return Math.pow(2, level) / total;
}

/** On track = fully funded in Abi's plan. */
export function isOnTrack(o: PriorityOutcome): boolean {
  return (o.abi?.completion ?? 0) >= 0.995;
}

/** The date a priority is (fully) funded, or the last due date it's measured against. */
export function fundedDate(f: PriorityFunding | undefined): PDate | undefined {
  if (!f) return undefined;
  return f.completedOn ?? (f.completion >= 0.995 ? f.lastDue : undefined);
}

export function laterOf(a?: PDate, b?: PDate): PDate | undefined {
  if (!a) return b;
  if (!b) return a;
  return compareDates(a, b) >= 0 ? a : b;
}
